/**
 * Step 5 — Data & Privacy.
 *
 * Ported from the FAIR app's AI Governance intake. This step carries the module's
 * privacy logic; all five of the source's conditional clusters are reproduced:
 *
 *   1. `isSpecialCategory` is offered per row only once that row is marked personal data
 *      or customer controlled (and is reset to false when the row goes back to not-personal).
 *   2. The privacy panel — legal basis, processing rationale, retention — appears whenever
 *      personal data is present OR possible, and those three are then required.
 *   3. A banner surfaces the highest-priority signal across every row — special category
 *      confirmed outranks personal data confirmed, which outranks personal data possible.
 *   4. `thirdPartyDetails` appears, and is required, only while `thirdPartySharing` is on.
 *   5. The DPA reminder is shown alongside third-party sharing.
 *
 * The GDPR legal-basis wording comes from LEGAL_BASIS_OPTIONS and is rendered in full —
 * abbreviating it would defeat the point of asking.
 *
 * `isPersonalData` is three-valued, not boolean: 'no' | 'yes' | 'customer_controlled'. A
 * file-upload or free-text field's content is decided by the customer at use time, and the
 * platform has no way to know in advance whether that content will be personal data —
 * forcing a Yes/No answer there means either guessing 'No' (understating the record's real
 * risk) or guessing 'Yes' (making every use case with a file upload look like a certain
 * personal-data processor, which isn't true either). 'customer_controlled' names that state
 * honestly instead of forcing a guess.
 *
 * `isSpecialCategory` stays a plain Yes/No, deliberately NOT given the same third answer:
 * Article 9 status is either flagged or it isn't, and a single toggle is the right control
 * for a plain boolean — offering a third "customer controlled" pill here would just be
 * copying the personal-data control onto a question that doesn't have that shape.
 */
import React from 'react';
import type { DataCategory, DataField, SensitivityLevel } from '../../../types/models';
import {
  DATA_CATEGORIES,
  DATA_CATEGORY_LABELS,
  DATA_SENSITIVITY_ANSWERS,
  DATA_SENSITIVITY_HINT,
  DATA_SENSITIVITY_LABELS,
  DATA_SENSITIVITY_TONE,
  DATA_SOURCE_SUGGESTIONS,
  LEGAL_BASIS_OPTIONS,
  SENSITIVITY_LABELS,
  SENSITIVITY_LEVELS,
} from '../../../constants/aiGovernance';
import { RepeatableRowCard, newRowId, useRepeatableRows } from '../../../components/RepeatableRows';
import type { RowAccent } from '../../../components/RepeatableRows';
import type { StepProps } from './types';

/** Required marker. Decorative — the accessible signal is `required` on the control itself. */
const Req: React.FC = () => (
  <span className="text-danger" aria-hidden="true">
    {' '}
    *
  </span>
);

const blankField = (): DataField => ({
  id: newRowId(),
  name: '',
  category: 'operational',
  isPersonalData: 'no',
  isSpecialCategory: false,
  sensitivity: 'internal',
  processingPurpose: '',
  dataSource: '',
});

const isBlankField = (f: DataField): boolean =>
  !f.name.trim() && !f.processingPurpose.trim() && !(f.dataSource ?? '').trim() && f.isPersonalData === 'no';

/**
 * The single highest-priority signal across every row, or null if nothing is flagged.
 * Priority order matches `dataFieldRiskCell` in `constants/aiGovernance.ts` — special
 * category outranks personal data, and confirmed outranks customer-controlled — because a
 * banner that disagreed with the per-row chips about which signal matters most would be
 * exactly the kind of drift that bit the AI Act ADM helper earlier this session.
 */
const describeDataRisk = (rows: DataField[]): { tone: string; content: React.ReactNode } | null => {
  if (rows.some(f => f.isSpecialCategory)) {
    return {
      tone: 'danger',
      content: (
        <>
          <strong>Special category data detected.</strong> This requires extra
          justification. Ensure legal has reviewed this before proceeding.
        </>
      ),
    };
  }
  if (rows.some(f => f.isPersonalData === 'yes')) {
    return {
      tone: 'warning',
      content: (
        <>
          <strong>Personal data detected.</strong> Complete the privacy fields below
          before continuing.
        </>
      ),
    };
  }
  if (rows.some(f => f.isPersonalData === 'customer_controlled')) {
    return {
      tone: 'info',
      content: (
        <>
          <strong>Personal data is possible.</strong> At least one field is customer
          controlled, so this AI may process personal data depending on what the customer
          uploads or enters. Complete the privacy fields below as a precaution.
        </>
      ),
    };
  }
  return null;
};

const Step5Data: React.FC<StepProps> = ({ useCase, update }) => {
  const { rows, add, canAdd, patch, remove, canRemove } = useRepeatableRows<DataField>({
    stored: useCase.dataFields,
    makeBlank: blankField,
    isBlank: isBlankField,
    onChange: dataFields => update({ dataFields }),
  });

  // Gates the privacy panel (Cluster 2) — 'customer_controlled' counts, matching the
  // server's has_personal check, so the precautionary fields are asked for even when the
  // answer isn't certain yet.
  const hasPersonalData = rows.some(f => f.isPersonalData !== 'no');
  const risk = describeDataRisk(rows);

  return (
    <div>
      <p className="np-sub mb-4">
        List the data this AI will process. Include everything it has access to, not just
        what it &quot;needs.&quot; Even reading emails or generating text from prompts counts.
      </p>

      {/* Cluster 3 — personal / special-category banner */}
      {risk && (
        <div className={`np-banner np-tone-${risk.tone} mb-3`}>
          <i className="ri-shield-user-line"></i>
          <div>{risk.content}</div>
        </div>
      )}

      {/* Data field inventory */}
      {rows.map((f, i) => (
        <RepeatableRowCard
          key={f.id}
          label="Field"
          index={i}
          accent={(
            f.isSpecialCategory ? 'danger'
              : f.isPersonalData !== 'no' ? DATA_SENSITIVITY_TONE[f.isPersonalData]
                : undefined
          ) as RowAccent | undefined}
          onRemove={canRemove ? () => remove(f.id) : undefined}
          chips={
            <>
              {f.isPersonalData !== 'no' && (
                <span className={`np-chip np-tone-${DATA_SENSITIVITY_TONE[f.isPersonalData]}`}>
                  {f.isPersonalData === 'yes' ? 'Personal' : 'Personal?'}
                </span>
              )}
              {f.isSpecialCategory && (
                <span className="np-chip np-tone-danger">Special Category</span>
              )}
            </>
          }
        >
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-5">
              <div className="np-field mb-0">
                <label htmlFor={`df-name-${f.id}`}>
                  Data field
                  <Req />
                </label>
                <input
                  id={`df-name-${f.id}`}
                  type="text"
                  className="np-input"
                  placeholder="e.g. Customer email, Invoice amount"
                  value={f.name}
                  required
                  onChange={e => patch(f.id, { name: e.target.value })}
                />
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="np-field mb-0">
                <label htmlFor={`df-cat-${f.id}`}>Category</label>
                <select
                  id={`df-cat-${f.id}`}
                  className="np-select"
                  value={f.category}
                  onChange={e => patch(f.id, { category: e.target.value as DataCategory })}
                >
                  {DATA_CATEGORIES.map(c => (
                    <option key={c} value={c}>
                      {DATA_CATEGORY_LABELS[c]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-6 col-md-4">
              <div className="np-field mb-0">
                <label htmlFor={`df-sens-${f.id}`}>Sensitivity</label>
                <select
                  id={`df-sens-${f.id}`}
                  className="np-select"
                  value={f.sensitivity}
                  onChange={e =>
                    patch(f.id, { sensitivity: e.target.value as SensitivityLevel })
                  }
                >
                  {SENSITIVITY_LEVELS.map(s => (
                    <option key={s} value={s}>
                      {SENSITIVITY_LABELS[s]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-12 col-md-6">
              <div className="np-field mb-0">
                <label htmlFor={`df-purpose-${f.id}`}>
                  Why does the AI need this field?
                  <Req />
                </label>
                <input
                  id={`df-purpose-${f.id}`}
                  type="text"
                  className="np-input"
                  value={f.processingPurpose}
                  required
                  onChange={e => patch(f.id, { processingPurpose: e.target.value })}
                />
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="np-field mb-0">
                <label htmlFor={`df-source-${f.id}`}>Data source</label>
                <input
                  id={`df-source-${f.id}`}
                  type="text"
                  className="np-input"
                  list={`df-source-list-${f.id}`}
                  placeholder="e.g. HubSpot CRM, call recording"
                  value={f.dataSource ?? ''}
                  onChange={e => patch(f.id, { dataSource: e.target.value })}
                />
                <datalist id={`df-source-list-${f.id}`}>
                  {DATA_SOURCE_SUGGESTIONS.map(s => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
              </div>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-12 col-md-6">
              <fieldset className="mb-0">
                <legend className="np-field-caption">Is this personal data?</legend>
                <div className="np-pill-toggle-group">
                  {DATA_SENSITIVITY_ANSWERS.map(answer => (
                    <button
                      key={answer}
                      type="button"
                      className={`np-pill-toggle${f.isPersonalData === answer ? ` active np-tone-${DATA_SENSITIVITY_TONE[answer]}` : ''}`}
                      aria-pressed={f.isPersonalData === answer}
                      onClick={() =>
                        // Going back to 'no' clears special category too — it is meaningless
                        // once personal data is ruled out, and a stale flag must not survive
                        // the toggle back. Moving to 'yes' or 'customer_controlled' leaves
                        // whatever special-category answer is already there untouched.
                        patch(f.id, answer === 'no'
                          ? { isPersonalData: answer, isSpecialCategory: false }
                          : { isPersonalData: answer })
                      }
                    >
                      {DATA_SENSITIVITY_LABELS[answer]}
                    </button>
                  ))}
                </div>
                {DATA_SENSITIVITY_HINT[f.isPersonalData] && (
                  <p className="np-hint mt-1 mb-0">{DATA_SENSITIVITY_HINT[f.isPersonalData]}</p>
                )}
              </fieldset>
            </div>

            {/* Cluster 1 — special category is only asked once personal data is at least
                possible, which now includes 'customer_controlled': if a field's personal-data
                status can't be ruled out, its special-category status can't be ruled out
                either. */}
            {f.isPersonalData !== 'no' && (
              <div className="col-12 col-md-6">
                <span className="np-field-caption">GDPR special category</span>
                <div className="d-flex align-items-center gap-2">
                  <label className="np-switch mb-0" htmlFor={`df-special-${f.id}`}>
                    <input
                      type="checkbox"
                      id={`df-special-${f.id}`}
                      checked={f.isSpecialCategory}
                      onChange={e => patch(f.id, { isSpecialCategory: e.target.checked })}
                    />
                    <span className="np-switch-track"></span>
                    <span className="np-switch-thumb"></span>
                  </label>
                  <label htmlFor={`df-special-${f.id}`} className="mb-0 small">
                    Special category <span className="np-sub">(health, biometric…)</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </RepeatableRowCard>
      ))}

      <div className="d-flex align-items-center gap-2 mb-4">
        <button
          type="button"
          className="np-btn np-btn-ghost np-btn-sm"
          onClick={() => add()}
          disabled={!canAdd}
          title={canAdd ? 'Add another data field' : 'Complete the field above first'}
        >
          <i className="ri-add-line"></i>Add data field
        </button>
        {!canAdd && <span className="np-hint m-0">Complete the field above to add another.</span>}
      </div>

      {/* Cluster 2 — the privacy panel exists only while personal data is in scope */}
      {hasPersonalData && (
        <div className="np-card np-card-accent-warning mb-3">
          <div className="np-card-head">
            <h2>
              <i className="ri-shield-user-line me-2"></i>Privacy requirements
            </h2>
          </div>
          <div className="np-card-body">
            <fieldset className="mb-4">
              <legend className="np-field-caption">
                Legal basis for processing
                <Req />
              </legend>
              <p className="np-hint mt-0 mb-2">
                Select the lawful reason for processing personal data:
              </p>
              <div className="np-choice-list">
                {LEGAL_BASIS_OPTIONS.map(opt => {
                  const selected = useCase.legalBasis === opt.value;
                  return (
                    <label
                      key={opt.value}
                      className={`np-choice${selected ? ' selected np-tone-primary' : ''}`}
                    >
                      <input
                        type="radio"
                        name="ai-legal-basis"
                        className="flex-shrink-0 mt-1"
                        value={opt.value}
                        checked={selected}
                        onChange={() => update({ legalBasis: opt.value })}
                      />
                      <div className="np-choice-body">
                        <div className="np-choice-label">{opt.label}</div>
                        <p className="np-choice-desc">{opt.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <div className="row g-3">
              <div className="col-12">
                <div className="np-field mb-0">
                  <label htmlFor="ai-processing-rationale">
                    Why is it necessary to use personal data?
                    <Req />
                  </label>
                  <textarea
                    id="ai-processing-rationale"
                    className="np-textarea"
                    rows={3}
                    placeholder="e.g. We need customer names and emails to match queries to account records. Anonymised data is insufficient because the AI must verify ownership before sharing order details."
                    value={useCase.processingRationale}
                    required
                    onChange={e => update({ processingRationale: e.target.value })}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="np-field mb-0">
                  <label htmlFor="ai-data-retention">
                    Data retention
                    <Req />
                  </label>
                  <input
                    id="ai-data-retention"
                    type="text"
                    className="np-input"
                    placeholder="e.g. 90 days, then auto-deleted"
                    value={useCase.dataRetention}
                    required
                    onChange={e => update({ dataRetention: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clusters 4 & 5 — third-party sharing, its DPA reminder and its details field */}
      <div className="np-card">
        <div className="np-card-body">
          <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
            <div>
              <span className="np-field-caption">External AI provider?</span>
              <p className="np-hint m-0">
                Will data be sent to an external provider (e.g. OpenAI, Google, Anthropic)?
              </p>
            </div>
            <div className="np-pill-toggle-group">
              <button
                type="button"
                className={`np-pill-toggle${useCase.thirdPartySharing ? ' active np-tone-warning' : ''}`}
                aria-pressed={useCase.thirdPartySharing}
                onClick={() => update({ thirdPartySharing: true })}
              >
                Yes
              </button>
              <button
                type="button"
                className={`np-pill-toggle${!useCase.thirdPartySharing ? ' active np-tone-neutral' : ''}`}
                aria-pressed={!useCase.thirdPartySharing}
                onClick={() => update({ thirdPartySharing: false, thirdPartyDetails: '' })}
              >
                No
              </button>
            </div>
          </div>

          {useCase.thirdPartySharing && (
            <>
              <div className="np-banner np-tone-danger mt-3">
                <i className="ri-alert-line"></i>
                <div>
                  Ensure a Data Processing Agreement (DPA) is in place before sharing any data.
                </div>
              </div>
              <div className="np-field mb-0 mt-3">
                <label htmlFor="ai-third-party-details">
                  Which provider? What data is shared and why?
                  <Req />
                </label>
                <textarea
                  id="ai-third-party-details"
                  className="np-textarea"
                  rows={3}
                  value={useCase.thirdPartyDetails}
                  required
                  onChange={e => update({ thirdPartyDetails: e.target.value })}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Step5Data;
